import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { Typography, Grid, CircularProgress, Divider } from '@mui/material';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { FP } from 'components/financepro/tokens';
import profileimage from 'assets/images/profileimage.webp';
import { apiPost } from 'lib/api';

function DetalleQrexpediente(props) {
  const { id, data } = useParams();
  const [imgsrc, setImgSrc] = useState('');
  const [result, setResult] = useState();
  const [comp, setComp] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiPost('/api/qrrecuperarexpediente', { chasis: data, id: id })
      .then(function ({ status, data: resp }) {
        if (status === 200) {
          setComp(resp['micomp'][0]);
          setResult(resp['data'][0]);
          setImgSrc(process.env.REACT_APP_SERVIDOR + `/api/photo/${id}/${resp['data'][0]['cedula']}`);
          setLoading(false);
        }
        if (status === 400) {
          alert(resp['Error']);
          setLoading(false);
        }
      })
      .catch(function () {
        setLoading(false);
      });
  }, []);

  const handleError = () => {
    setImgSrc(profileimage);
  };

  const detalles = result
    ? [
        { label: 'Cédula', value: result['cedula'], icon: BadgeRoundedIcon },
        { label: 'Chasis', value: result['chasis'], icon: ConfirmationNumberRoundedIcon },
        { label: 'Marca', value: result['marca'], icon: DirectionsCarRoundedIcon },
        { label: 'Modelo', value: result['modelo'], icon: LocalOfferRoundedIcon },
        { label: 'Color', value: result['colore'], icon: PaletteRoundedIcon },
        { label: 'Teléfono', value: comp?.['telefono'], icon: PhoneRoundedIcon },
      ]
    : [];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
        px: { xs: 1.5, sm: 3 },
        py: { xs: 3, sm: 5 },
        boxSizing: 'border-box',
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: '60vh',
            color: FP.color.textSecondary,
          }}
        >
          <CircularProgress sx={{ color: FP.color.accentBlue }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Cargando expediente…
          </Typography>
        </Box>
      ) : result ? (
        <Box
          sx={{
            maxWidth: 560,
            mx: 'auto',
            borderRadius: `${FP.radius.xl}px`,
            overflow: 'hidden',
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.md,
          }}
        >
          {/* Branded hero */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${FP.color.navy} 0%, ${FP.color.navySoft} 100%)`,
              px: { xs: 2.5, sm: 4 },
              py: { xs: 3, sm: 3.5 },
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessRoundedIcon sx={{ color: FP.color.accentBlueSoft, fontSize: 20 }} />
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  letterSpacing: '-0.01em',
                  wordBreak: 'break-word',
                }}
              >
                {comp?.['ncomp'] || 'Expediente'}
              </Typography>
            </Box>
            <Typography
              sx={{
                color: FP.color.textMuted,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                mt: 0.5,
              }}
            >
              Ficha de expediente
            </Typography>
          </Box>

          {/* Avatar + identity, overlapping the hero */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 4 },
              pt: { xs: 2.5, sm: 3 },
              textAlign: 'center',
            }}
          >
            <Box
              component="img"
              onError={handleError}
              src={imgsrc}
              alt="perfil"
              sx={{
                width: { xs: 104, sm: 120 },
                height: { xs: 104, sm: 120 },
                borderRadius: '50%',
                objectFit: 'cover',
                bgcolor: 'grey.300',
                border: '4px solid #fff',
                boxShadow: FP.shadow.soft,
              }}
            />
            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 800,
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                color: FP.color.textPrimary,
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {result['nombres']}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                fontWeight: 600,
                color: FP.color.textSecondary,
                mt: 0.25,
              }}
            >
              {result['cedula']}
            </Typography>
          </Box>

          <Divider sx={{ mt: 3, mx: { xs: 2.5, sm: 4 }, borderColor: FP.color.border }} />

          {/* Detail grid */}
          <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 } }}>
            <Grid container spacing={1.5}>
              {detalles.map(({ label, value, icon: Icon }) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: `${FP.radius.md}px`,
                      bgcolor: FP.color.surfaceAlt,
                      height: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 38,
                        height: 38,
                        borderRadius: `${FP.radius.sm}px`,
                        bgcolor: FP.color.accentBlueSoft,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ fontSize: 20, color: FP.color.accentBlue }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: FP.color.textMuted,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.92rem',
                          fontWeight: 600,
                          color: FP.color.textPrimary,
                          wordBreak: 'break-word',
                        }}
                      >
                        {value ?? '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            color: FP.color.textSecondary,
            textAlign: 'center',
            gap: 1,
          }}
        >
          <ConfirmationNumberRoundedIcon sx={{ fontSize: 44, color: FP.color.textMuted }} />
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
            Expediente no disponible
          </Typography>
          <Typography sx={{ fontSize: '0.85rem' }}>
            No se encontró información para este código.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DetalleQrexpediente;
